<!--
  Copyright (C) 2025 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
-->

<template>
  <div class="audio-player-container">
    <div class="audio-player-content">
      <!-- Audio visualization -->
      <div class="audio-visual">
        <div class="audio-icon">
          <i class="ri-music-2-line"></i>
        </div>
        <div class="audio-name">{{ contentNode?.name }}</div>
      </div>

      <!-- Audio controls -->
      <div class="audio-controls">
        <audio
          ref="audioRef"
          :key="contentUrl"
          :src="contentUrl"
          controls
          class="audio-element"
          @play="onPlay"
          @pause="onPause"
          @ended="onEnded"
          @timeupdate="onTimeUpdate"
          @loadedmetadata="onLoadedMetadata"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { ref } from "vue";

export default {
  name: "AudioPlayer",
  props: {
    contentUrl: {
      type: String,
      default: null,
    },
    contentNode: {
      type: Object,
      default: null,
    },
  },
  emits: ["play", "pause", "ended"],
  setup(props, { emit }) {
    const audioRef = ref(null);
    const isPlaying = ref(false);
    const currentTime = ref(0);
    const duration = ref(0);

    const onPlay = () => {
      isPlaying.value = true;
      emit("play");
    };

    const onPause = () => {
      isPlaying.value = false;
      emit("pause");
    };

    const onEnded = () => {
      isPlaying.value = false;
      emit("ended");
    };

    const onTimeUpdate = () => {
      if (audioRef.value) {
        currentTime.value = audioRef.value.currentTime;
      }
    };

    const onLoadedMetadata = () => {
      if (audioRef.value) {
        duration.value = audioRef.value.duration;
      }
    };

    return {
      audioRef,
      isPlaying,
      currentTime,
      duration,
      onPlay,
      onPause,
      onEnded,
      onTimeUpdate,
      onLoadedMetadata,
    };
  },
};
</script>

<style lang="scss" scoped>
.audio-player-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 2rem;
  background: var(--p-surface-ground);
}

.audio-player-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
  padding: 2rem;
  background: var(--p-surface-card);
  border: 1px solid var(--p-surface-border);
  border-radius: 1rem;
  max-width: 100%;
  width: 400px;
}

.audio-visual {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}

.audio-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 6rem;
  height: 6rem;
  border-radius: 50%;
  background: var(--p-primary-100);
  color: var(--p-primary-color);

  i {
    font-size: 3rem;
  }
}

.audio-name {
  font-size: 1rem;
  font-weight: 600;
  color: var(--p-text-color);
  text-align: center;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.audio-controls {
  width: 100%;
}

.audio-element {
  width: 100%;
  border-radius: 0.5rem;
}
</style>
